import React, { useMemo, useState } from 'react';
import type { QuestionConfig } from '../types';
import { SurveyHubHero } from './SurveyHubHero';
import '../survey-hub.css';

interface Props {
  onAccept: (questions: QuestionConfig[], audience: string, language: string) => void;
  onBack: () => void;
}

const QUESTION_TYPES: Array<[QuestionConfig['question_type'], string]> = [
  ['text', 'Открытый текст'],
  ['single', 'Одиночный выбор'],
  ['multi', 'Множественный выбор'],
  ['scale', 'Шкала'],
  ['matrix', 'Матрица'],
  ['date', 'Дата'],
];

type Row = { id: string; type: string; text: string; mapping: string };

const SEED: Row[] = [
  { id: '1', type: 'single', text: 'Укажите ваш курс обучения', mapping: '1 / 2 / 3 курс' },
  { id: '2', type: 'single', text: 'Насколько понятен индивидуальный план?', mapping: '3 варианта' },
  { id: '3', type: 'text', text: 'Какие сложности возникли?', mapping: 'Открытый текст' },
  { id: '4', type: 'scale', text: 'Доступность консультаций 1–5', mapping: 'Шкала 1…5' },
  { id: '5', type: 'multi', text: 'Какие направления поддержки нужны?', mapping: 'Множественный выбор' },
  { id: '6', type: 'matrix', text: 'Публикации / стажировка / практика × 1–5', mapping: 'Матрица' },
  { id: '7', type: 'date', text: 'Дата последней консультации', mapping: 'Дата' },
];

/** Structure recognition — template mapping(). */
export const StructureMappingView: React.FC<Props> = ({ onAccept, onBack }) => {
  const [rows, setRows] = useState<Row[]>(SEED);
  const [audience, setAudience] = useState('Докторанты · 1 курс');
  const [language, setLanguage] = useState('RU');

  const recognized = useMemo(() => rows.length, [rows]);

  const accept = () => {
    const questions: QuestionConfig[] = rows.map((r, i) => ({
      order_index: i + 1,
      question_type: (r.type as QuestionConfig['question_type']) || 'text',
      title: r.text,
      prompt: r.mapping,
      is_required: true,
      options: [],
    }));
    onAccept(questions, audience, language);
  };

  return (
    <div className="survey-hub-content">
      <SurveyHubHero
        title="Распознавание структуры"
        description="Система классифицирует типы вопросов и показывает сопоставление полей. Уточняет только блокирующую неоднозначность — без лишних вопросов."
      />

      <section className="survey-hub-panel">
        <header>
          <div className="grow">
            <h3>Предложенное сопоставление полей</h3>
            <p>Открытый / одиночный / множественный / шкала / матрица / дата</p>
          </div>
          <span className="survey-hub-chip ok">
            {recognized}/{recognized} распознано
          </span>
        </header>
        <div className="survey-hub-table-scroll">
          <table className="survey-hub-map-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Тип</th>
                <th>Текст</th>
                <th>Сопоставление</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>
                    <select
                      value={r.type}
                      onChange={(e) => {
                        const next = [...rows];
                        next[idx] = { ...r, type: e.target.value };
                        setRows(next);
                      }}
                    >
                      {QUESTION_TYPES.map(([code, label]) => (
                        <option key={code} value={code}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{r.text}</td>
                  <td>{r.mapping}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="survey-hub-panel">
        <header>
          <div className="grow">
            <h3>Только один блокирующий вопрос</h3>
            <p>Система спрашивает только то, без чего нельзя корректно опубликовать форму.</p>
          </div>
          <span className="survey-hub-chip gold">Обязательно</span>
        </header>
        <div className="survey-hub-form">
          <div className="survey-hub-field">
            <label>Аудитория</label>
            <select value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option>Докторанты · 1 курс</option>
              <option>Докторанты · 2 курс</option>
              <option>Докторанты · 3 курс</option>
              <option>Все докторанты</option>
            </select>
          </div>
          <div className="survey-hub-field">
            <label>Язык формы</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option>RU</option>
              <option>KZ</option>
              <option>EN</option>
            </select>
          </div>
        </div>
        <div className="survey-hub-actions">
          <button type="button" className="survey-hub-btn" onClick={onBack}>
            ← Назад
          </button>
          <button type="button" className="survey-hub-btn primary" onClick={accept}>
            Принять сопоставление и редактировать →
          </button>
        </div>
      </section>
    </div>
  );
};
